import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AgentLookupDto } from 'src/core/models/agent/agent-lookup.dto';
import { AgentListItemDto } from 'src/core/models/agent/agent-list-item.dto';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';

const NAME_CACHE_MAX_SIZE = 200;

@Injectable({
  providedIn: 'root',
})
export class AgentService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apis.default.url.replace(/\/$/, '');

  private readonly nameCache = new Map<string, string>();

  cacheAgentNames(agents: AgentListItemDto[]): void {
    agents.forEach(a => this.nameCache.set(String(a.id), a.name?.trim() || a.userName?.trim() || ''));
    if (this.nameCache.size > NAME_CACHE_MAX_SIZE) {
      const overflow = this.nameCache.size - NAME_CACHE_MAX_SIZE;
      const iter = this.nameCache.keys();
      for (let i = 0; i < overflow; i++) {
        this.nameCache.delete(iter.next().value!);
      }
    }
  }

  getCachedAgentName(id: string): string | undefined {
    return this.nameCache.get(id);
  }

  /**
   * Lists the tenant's agents for selection controls.
   * @param availableFirst Orders agents in the Available state to the top, the rest alphabetically after
   * them. The ordering is applied by the backend query; opt in only where the screen asks for it, so the
   * plain alphabetical directory stays the default.
   */
  getAgentLookup(availableFirst = false): Observable<AgentLookupDto[]> {
    return this.http.get<AgentLookupDto[]>(
      `${this.baseUrl}/api/agentic-cx/agent/agent-lookup`,
      availableFirst ? { params: { availableFirst: true } } : {},
    );
  }

  getAgents(request: ListRequestDto): Observable<ListResponseDto<AgentListItemDto>> {
    return this.http.get<ListResponseDto<AgentListItemDto>>(
      `${this.baseUrl}/api/agentic-cx/agent/agents`,
      {
        params: {
          filters: JSON.stringify(request.filters ?? []),
          sorters: JSON.stringify(request.sorters ?? []),
          skipCount: String(request.skipCount ?? 0),
          maxResultCount: String(request.maxResultCount ?? 10),
        },
      },
    );
  }
}
